import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {formatLastName} from "../../../constants";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { useTheme } from "../../../contexts/ThemeContext";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { fetchTeamRoster } from "../../../utils/fetchTeamRoster";

function BracketClassic({
                                           draw,
                                           tournamentGrade,
                                           setModalOpen,
                                           setModalData,
                                           pickerTarget,
                                           pickedCandidate,
                                           onPlaceCandidate,
                                           onCancelCandidate,
                                           mainGridNumber,
                                           onDeleteParticipant,
                                           expandMatch,
                                           is_authorized,
                                           canEdit,
                                           updateMatches,
                                           onMoveFailed,
                                           searchChips = [],
                                           teamRoster,
                                           playerNames,
                                           loadingCells
                                       }) {
    const api = useAxios();
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const { compactTable } = useTheme();
    const history = useHistory();
    const location = useLocation();
    const [actionsModal, setActionsModal] = useState(null);
    const [pickedSlot, setPickedSlot] = useState(null);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    // slots currently mid-move (keyed by "matchId_side") - the optimistic update in
    // movePlayer makes the swap itself feel instant, but on heavier brackets the
    // surrounding re-render can still lag; this drives a small loader (same look as
    // MatchStatusSelect's status-loader) on the two affected slots as a fallback
    // so a slow paint doesn't read as a dead tap/drop
    const [loadingSlots, setLoadingSlots] = useState({});
    const longPressTimer = useRef(null);
    // Separate from longPressTimer above (the admin drag-and-drop pickup
    // gesture, mutually exclusive with this one - that's is_authorized-only,
    // this is public-view-only) so the two never fight over the same timer.
    const nameLongPressTimer = useRef(null);
    // Tracks whether the timer below actually fired for the touch currently
    // in progress - the synthetic click Android/iOS still dispatch after
    // touchend needs to know not to also treat itself as "go to the match"
    // once the long-press has already opened the roster menu.
    const nameLongPressFiredRef = useRef(false);

    useEffect(() => {
        const onTouch = () => setIsTouchDevice(true);
        window.addEventListener("touchstart", onTouch, { once: true });
        return () => window.removeEventListener("touchstart", onTouch);
    }, []);

    // === БАЗОВЫЕ ВЫЧИСЛЕНИЯ ===
    const N = useMemo(() => Math.pow(2, Math.ceil(Math.log2(Math.max(2, draw.number_players)))), [draw.number_players]);
    const R = useMemo(() => Math.log2(N), [N]);
    const isMainDraw = draw.number === mainGridNumber;
    const hasAnySeed = useMemo(() => (draw.matches || []).some(m => m.round_number === 1 && (m.seed1 || m.seed2)), [draw.matches]);

    const matchByNum = useMemo(() => {
        const byNum = {};
        (draw.matches || []).forEach(m => byNum[m.match_number] = m);
        return byNum;
    }, [draw.matches]);

    const matchById = useMemo(() => {
        const byId = {};
        (draw.matches || []).forEach(m => { byId[m.id] = m; });
        return byId;
    }, [draw.matches]);

    const matchOffset = useMemo(() => {
        const nums = (draw.matches || []).map(m => m.match_number);
        return nums.length ? Math.min(...nums) - 1 : 0;
    }, [draw.matches]);

    const matchStart = useCallback(r => matchOffset + N - (N >> (r - 1)), [N, matchOffset]);

    const roundLabel = useCallback((r) => {
        if (r === R) return t("Final");
        if (i18n.language === "en") {
            if (r === R - 1) return "SF";
            if (r === R - 2) return "QF";
            return `R${r}`;
        }
        if (r === R - 1) return "1/2";
        if (r === R - 2) return "1/4";
        return `1/${2 ** (R - r)}`;
    }, [R, t, i18n.language]);

    // === ОБРАБОТЧИКИ СОБЫТИЙ ===
    const handleSeedChange = useCallback((matchId, side, value) => {
        const payloadValue = value.trim() === "" ? "" : value.trim();
        api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${matchId}/`, {
            [side === 1 ? "seed1" : "seed2"]: payloadValue
        }).catch(err => console.error("Error:", err));
    }, [api, id]);

    const handlePlayerClick = useCallback((match, side) => {
        if (!isMainDraw || match.round_number !== 1) return;
        setModalData({ matchId: match.id, side, isAlreadyInMatch: match.x1 || match.x2 });
        setModalOpen(true);
    }, [isMainDraw, setModalData, setModalOpen]);

    const handleMatchClick = useCallback((matchNum) => {
        const m = matchByNum[matchNum];
        if (m) expandMatch(m.match_number);
    }, [matchByNum, expandMatch]);

    const hasParticipant = useCallback((match, side) => {
        if (side === 1 ? match.x1 : match.x2) return true;
        if (tournamentGrade === 1) return !!(side === 1 ? match.player_1_name : match.player_2_name);
        if (tournamentGrade === 2) return !!((side === 1 ? match.player_1_name : match.player_2_name) && (side === 1 ? match.player_3_name : match.player_4_name));
        return !!(side === 1 ? match.team_1_name || match.team_1 : match.team_2_name || match.team_2);
    }, [tournamentGrade]);

    const extractSlotData = useCallback((match, side) => {
        const isBye = side === 1 ? match.x1 : match.x2;
        if (isBye) return { isBye: true };
        if (tournamentGrade === 1) return {
            player: side === 1 ? match.player_1 : match.player_2,
            player_name: side === 1 ? match.player_1_name : match.player_2_name
        };
        if (tournamentGrade === 2) return {
            p1: side === 1 ? match.player_1 : match.player_2,
            p2: side === 1 ? match.player_3 : match.player_4,
            p1_name: side === 1 ? match.player_1_name : match.player_2_name,
            p2_name: side === 1 ? match.player_3_name : match.player_4_name
        };
        return {
            team: side === 1 ? match.team_1 : match.team_2,
            team_name: side === 1 ? match.team_1_name : match.team_2_name,
            team_number: side === 1 ? match.team_1_number : match.team_2_number
        };
    }, [tournamentGrade]);

    const buildSlotPayload = useCallback((data, side) => {
        if (data.isBye) return side === 1 ? { x1: true } : { x2: true };
        if (tournamentGrade === 1) return side === 1 ? { player_1: data.player } : { player_2: data.player };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: data.p1, player_3: data.p2 }
            : { player_2: data.p1, player_4: data.p2 };
        return side === 1 ? { team_1: data.team } : { team_2: data.team };
    }, [tournamentGrade]);

    // like buildSlotPayload, but also carries the *_name fields so the move
    // can be reflected in local state immediately (see movePlayer) instead of
    // waiting on the round trip - the API payload itself only needs the ids
    const buildOptimisticSlotPayload = useCallback((data, side) => {
        if (data.isBye) return side === 1 ? { x1: true } : { x2: true };
        if (tournamentGrade === 1) return side === 1
            ? { player_1: data.player, player_1_name: data.player_name, x1: null }
            : { player_2: data.player, player_2_name: data.player_name, x2: null };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: data.p1, player_3: data.p2, player_1_name: data.p1_name, player_3_name: data.p2_name, x1: null }
            : { player_2: data.p1, player_4: data.p2, player_2_name: data.p1_name, player_4_name: data.p2_name, x2: null };
        return side === 1
            ? { team_1: data.team, team_1_name: data.team_name, team_1_number: data.team_number, x1: null }
            : { team_2: data.team, team_2_name: data.team_name, team_2_number: data.team_number, x2: null };
    }, [tournamentGrade]);

    const clearSlotPayload = useCallback((side) => {
        if (tournamentGrade === 1) return side === 1
            ? { player_1: null, player_1_name: null, x1: null }
            : { player_2: null, player_2_name: null, x2: null };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: null, player_3: null, player_1_name: null, player_3_name: null, x1: null }
            : { player_2: null, player_4: null, player_2_name: null, player_4_name: null, x2: null };
        return side === 1
            ? { team_1: null, team_1_name: null, team_1_number: null, x1: null }
            : { team_2: null, team_2_name: null, team_2_number: null, x2: null };
    }, [tournamentGrade]);

    const slotKey = (matchId, side) => `${matchId}_${side}`;

    const movePlayer = useCallback(async (src, target) => {
        if (src.matchId === target.matchId && src.side === target.side) return;
        const srcMatch = matchById[src.matchId];
        const targetMatch = matchById[target.matchId];
        if (!srcMatch || !targetMatch) return;

        const srcData = extractSlotData(srcMatch, src.side);
        const targetOccupied = hasParticipant(targetMatch, target.side);
        const targetData = targetOccupied ? extractSlotData(targetMatch, target.side) : null;

        const srcKey = slotKey(src.matchId, src.side);
        const targetKey = slotKey(target.matchId, target.side);
        setLoadingSlots(prev => ({ ...prev, [srcKey]: true, [targetKey]: true }));

        // optimistic update: reflect the swap in local state right away so it
        // reads as instant on tap/drop, same as the long-press flow in groups
        // (TournamentGroups.movePlayer) - reconciled below with the API's
        // authoritative response, and rolled back via onMoveFailed on error
        updateMatches([
            { id: target.matchId, ...buildOptimisticSlotPayload(srcData, target.side) },
            { id: src.matchId, ...(targetOccupied ? buildOptimisticSlotPayload(targetData, src.side) : clearSlotPayload(src.side)) }
        ], false);

        try {
            if (targetOccupied) {
                const r1 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${src.matchId}/`, buildSlotPayload(targetData, src.side));
                // fetch_pl=false: this is a swap between two already-placed slots in
                // the same draw, the tournament's participant pool didn't change.
                // Doing it on every intermediate update (previously the default on
                // both calls) fires an extra fetchParticipants() network round-trip
                // per step, which is what made repeated long-press moves feel like
                // the page was hanging - one refetch after the whole swap is enough
                updateMatches(r1.data.updated_matches, false);
                const r2 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${target.matchId}/`, buildSlotPayload(srcData, target.side));
                updateMatches(r2.data.updated_matches);
            } else {
                const r1 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${target.matchId}/`, buildSlotPayload(srcData, target.side));
                updateMatches(r1.data.updated_matches, false);
                const r2 = await api.delete(`/api/v1.0/tournaments/${id}/draws/matches/${src.matchId}/?side=${src.side}`);
                if (r2?.data?.updated_matches) updateMatches(r2.data.updated_matches);
            }
        } catch (e) {
            console.error("Failed to move participant:", e);
            onMoveFailed?.();
        } finally {
            setLoadingSlots(prev => {
                const next = { ...prev };
                delete next[srcKey];
                delete next[targetKey];
                return next;
            });
        }
    }, [matchById, extractSlotData, buildSlotPayload, buildOptimisticSlotPayload, clearSlotPayload, hasParticipant, api, id, updateMatches, onMoveFailed]);

    const handleCellTap = useCallback((matchId, side) => {
        if (!pickedSlot) return;

        const src = pickedSlot;
        setPickedSlot(null);

        // tapping the source slot again cancels the pick instead of moving it
        if (src.matchId === matchId && src.side === side) return;
        movePlayer(src, { matchId, side });
    }, [pickedSlot, movePlayer]);

    const handleDragStart = useCallback((e, matchId, side) => {
        e.dataTransfer.setData("matchId", matchId);
        e.dataTransfer.setData("side", String(side));
        e.dataTransfer.effectAllowed = "move";
    }, []);

    const handleDrop = useCallback((e, matchId, side) => {
        e.preventDefault();
        const srcMatchId = e.dataTransfer.getData("matchId");
        const srcSide = Number(e.dataTransfer.getData("side"));
        if (!srcMatchId) return;
        movePlayer({ matchId: srcMatchId, side: srcSide }, { matchId, side });
    }, [movePlayer]);

    const handlePlClick = (pId, history, location) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", pId);
        // replace, not push - same reasoning as Bracket.js's own handlePlClick.
        history.replace({ search: params.toString() });
    };

    const buildProfile = useCallback((player, pid) => ({
        icon: "user",
        label: `${player?.first_name ? player.first_name[0] + '. ' : ''}${formatLastName(player?.last_name)}`,
        onClick: () => handlePlClick(pid, history, location)
    }), [history, location]);

    const buildDeleteAction = useCallback((match, side) => ({
        icon: "trash-2",
        label: t("Delete"),
        danger: true,
        onClick: async () => {
            try {
                const response = await api.delete(`/api/v1.0/tournaments/${id}/draws/matches/${match.id}/?side=${side}`);
                onDeleteParticipant(response);
            } catch (e) {
                console.error(e);
                onDeleteParticipant(null);
            }
        }
    }), [api, id, t, onDeleteParticipant]);

    // "Who's in this slot" menu - open to every viewer (see canOpenActions
    // above), not just admins editing the main draw. Grade 3's roster isn't
    // on the match object (only team_1/team_1_name are) - fetched on demand
    // via fetchTeamRoster, same as Групи/Розклад's own team menus.
    // allowMove: only passed true when opened (via click) on an editable
    // round-1 slot - prepends a "Move" entry that hands off into the exact
    // same pickedSlot drag flow the long-press gesture (handleTouchStart
    // below) already starts directly - offered here too so it doesn't
    // require knowing that gesture, same as Groups' own menu. Never offered
    // on a placement draw/later round with no move of its own.
    const openParticipantMenu = useCallback(async (match, side, { allowMove = false } = {}) => {
        if (!match) return;
        const deleteAction = is_authorized ? [buildDeleteAction(match, side)] : [];
        const moveAction = allowMove ? [{
            icon: "move",
            label: t("Move"),
            onClick: () => setPickedSlot({ matchId: match.id, side })
        }] : [];
        // Offered whenever both sides are filled (same condition bothPlayersExist
        // in bodyRows uses to let a plain click on the rest of the cell open the
        // match) - on a 2-slot bracket the round-1 match is also the whole
        // draw's only match, and its slots are editable, so a plain click on the
        // name never reaches handleCellClick (it's swallowed by this very menu
        // below instead) - there was previously no way at all to reach that
        // match from here.
        const matchAction = hasParticipant(match, 1) && hasParticipant(match, 2) ? [{
            iconText: "-:-",
            label: t("Match"),
            onClick: () => handleMatchClick(match.match_number)
        }] : [];

        // A Bye has no player/team name to build a title or profile action
        // from (the grade-specific branches below all bail on that) - it
        // still needs to be removable though, so handle it before them with
        // just the Delete action instead of falling through to their own
        // "no name, nothing to show" early returns.
        const isBye = side === 1 ? match.x1 : match.x2;
        if (isBye) {
            if (!deleteAction.length && !moveAction.length && !matchAction.length) return;
            setActionsModal({ title: t("Bye"), actions: [...matchAction, ...moveAction, ...deleteAction] });
            return;
        }

        if (tournamentGrade === 1) {
            const p = side === 1 ? match.player_1_name : match.player_2_name;
            const pid = side === 1 ? match.player_1 : match.player_2;
            if (!p || typeof p === "string") return;
            const title = `${p.first_name || ""} ${formatLastName(p.last_name)}`.trim();
            setActionsModal({ title, actions: [...matchAction, buildProfile(p, pid), ...moveAction, ...deleteAction] });
            return;
        }

        if (tournamentGrade === 2) {
            const p1 = side === 1 ? match.player_1_name : match.player_2_name;
            const p2 = side === 1 ? match.player_3_name : match.player_4_name;
            const id1 = side === 1 ? match.player_1 : match.player_2;
            const id2 = side === 1 ? match.player_3 : match.player_4;
            const title = [p1, p2].filter(Boolean).map(p => formatLastName(p.last_name)).join(" / ") || t("Participants");
            const profiles = [];
            if (p1) profiles.push(buildProfile(p1, id1));
            if (p2) profiles.push(buildProfile(p2, id2));
            setActionsModal({ title, actions: [...matchAction, ...profiles, ...moveAction, ...deleteAction] });
            return;
        }

        const teamId = side === 1 ? match.team_1 : match.team_2;
        const title = side === 1
            ? (match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`))
            : (match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`));
        setActionsModal({ title, actions: [{ icon: "loader", label: t("Loading..."), onClick: () => {} }] });
        const roster = await fetchTeamRoster(api, id, typeof teamId === "object" ? teamId?.id : teamId);
        setActionsModal({ title, actions: [...matchAction, ...roster.map(p => buildProfile(p, p.player_id || p.id)), ...moveAction, ...deleteAction] });
    }, [tournamentGrade, is_authorized, buildProfile, buildDeleteAction, hasParticipant, handleMatchClick, api, id, t]);

    // Long-press picks the slot up for a move directly, same as Groups' own
    // handlePlayerTouchStart - the menu (with its own "Move" entry, see
    // openParticipantMenu's allowMove param) is reached from a plain click
    // instead, not this gesture.
    const handleTouchStart = useCallback((e, matchId, side) => {
        if (!isTouchDevice) setIsTouchDevice(true);
        if (pickedSlot) return;
        // Can't start moving an already-placed participant while the
        // add-picker panel is docked open targeting a slot - see the same
        // guard in TournamentGroups.js.
        if (pickerTarget) return;
        // ...or while a candidate is picked from the panel, waiting for a
        // destination tap - same reasoning, different picked-state.
        if (pickedCandidate) return;

        clearTimeout(longPressTimer.current);
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;

        const timer = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            setPickedSlot({ matchId, side });
        }, 400);

        longPressTimer.current = timer;
        const target = e.currentTarget;

        const checkMove = (eMove) => {
            const touchMove = eMove.touches[0];
            if (Math.abs(touchMove.clientX - startX) > 10 || Math.abs(touchMove.clientY - startY) > 10) {
                clearTimeout(longPressTimer.current);
            }
        };

        const cleanup = () => {
            clearTimeout(longPressTimer.current);
            target.removeEventListener("touchmove", checkMove);
            target.removeEventListener("touchend", cleanup);
            target.removeEventListener("touchcancel", cleanup);
        };

        target.addEventListener("touchmove", checkMove, { passive: true });
        target.addEventListener("touchend", cleanup, { once: true });
        target.addEventListener("touchcancel", cleanup, { once: true });
    }, [isTouchDevice, pickedSlot, pickerTarget, pickedCandidate]);

    // Team tournaments (grade 3), public view only: a tap on the team name
    // now goes straight to the match (same as tapping anywhere else in the
    // cell) - the roster menu instead needs a deliberate long-press, same
    // 400ms/10px-tolerance shape as the admin drag-pickup gesture above.
    // Desktop mouse clicks have no long-press to fall back on, so
    // handleNameClick below leaves those opening the menu on a plain click.
    const handleNameTouchStart = useCallback((e, match, side) => {
        if (pickedSlot) return;
        nameLongPressFiredRef.current = false;
        clearTimeout(nameLongPressTimer.current);
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;

        const timer = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            nameLongPressFiredRef.current = true;
            openParticipantMenu(match, side);
        }, 400);

        nameLongPressTimer.current = timer;
        const target = e.currentTarget;

        const checkMove = (eMove) => {
            const touchMove = eMove.touches[0];
            if (Math.abs(touchMove.clientX - startX) > 10 || Math.abs(touchMove.clientY - startY) > 10) {
                clearTimeout(nameLongPressTimer.current);
            }
        };

        const cleanup = () => {
            clearTimeout(nameLongPressTimer.current);
            target.removeEventListener("touchmove", checkMove);
            target.removeEventListener("touchend", cleanup);
            target.removeEventListener("touchcancel", cleanup);
        };

        target.addEventListener("touchmove", checkMove, { passive: true });
        target.addEventListener("touchend", cleanup, { once: true });
        target.addEventListener("touchcancel", cleanup, { once: true });
    }, [pickedSlot, openParticipantMenu]);

    // === РЕНДЕР: ИГРОКИ, ПОСЕВ, СЧЕТ ===
    const renderSource = useCallback((match, side) => {
        const src = side === 1 ? match.source_match_1 : match.source_match_2;
        if (!src) return null;
        const label = match.is_source_winner ? t("Winner") : t("Loser");
        const className = match.is_source_winner ? "winner_color" : "loser_color";
        return <span className={className} style={{cursor: "pointer"}} onClick={(e) => { e.stopPropagation(); handleMatchClick(src); }}>{label} #{src}</span>;
    }, [t, handleMatchClick]);

    // Every participant name belonging to a match (both sides, grade-aware) -
    // used to dim/highlight the match's whole ps_ cell (name + score + #N
    // badge together, see its own className below) as one block, rather than
    // just the name text the way renderSide's wrap() used to on its own.
    // match.player_X_name only carries a single-letter first_name (display-only
    // backend quirk, same as the groups endpoint - see fetchPlayerNameMap.js's
    // own comment); resolve the full name from the prefetched playerNames map
    // (keyed by player id) so a full-first-name search chip still matches,
    // without touching the intentional initial shown in renderSide below.
    const resolvePlayer = useCallback((p, pid) => {
        const key = pid || p?.id;
        return (p && key) ? (playerNames?.get(key) || p) : p;
    }, [playerNames]);

    const getMatchNames = useCallback((match) => {
        if (tournamentGrade === 1) return [resolvePlayer(match.player_1_name, match.player_1), resolvePlayer(match.player_2_name, match.player_2)];
        if (tournamentGrade === 2) return [
            resolvePlayer(match.player_1_name, match.player_1), resolvePlayer(match.player_3_name, match.player_3),
            resolvePlayer(match.player_2_name, match.player_2), resolvePlayer(match.player_4_name, match.player_4)
        ];
        const team1 = match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`);
        const team2 = match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`);
        return [team1, ...(teamRoster?.get(team1) || []), team2, ...(teamRoster?.get(team2) || [])];
    }, [tournamentGrade, teamRoster, resolvePlayer, t]);

    const matchSearchState = useCallback((match) => (
        match ? chipsMatchEntity(searchChips, { names: getMatchNames(match), drawValue: draw.number }) : null
    ), [getMatchNames, searchChips, draw.number]);

    const renderSide = useCallback((match, side, isMainDraw = false, slotNumber = null, isPickerTarget = false) => {
        // a bye never gets its own winner_side (there's no match to record a
        // result on) - the side facing it still advances automatically, so
        // it reads as a win too, not the same "no result yet" blank as an
        // actually-unplayed match.
        const opponentIsBye = side === 1 ? match.x2 : match.x1;
        const isWinner = match.winner_side === side || opponentIsBye;
        const isLoser = match.winner_side !== null && match.winner_side !== side;
        const sideClass = isWinner ? "match-winner" : (isLoser ? "match-loser" : "");

        // Dimming/highlighting itself now lives on the outer ps_ cell (covers
        // the score and #N badge alongside this name, not just the name) -
        // see its own className below.
        // noPadding - the group-add-participant button (below) already carries
        // its own padding/sizing (same class as TournamentGroupTable.js's
        // identical button, unwrapped there) - this div's own 4px/8px padding
        // stacked on top of it just pushed the button off-center from the seed
        // input next to it for no reason, the one thing this whole wrap() exists
        // to avoid for actual names.
        const wrap = (content, allowOverflow = false, noPadding = false) => (
            <div className={sideClass} style={{ overflow: allowOverflow ? "visible" : "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: noPadding ? undefined : "140px", padding: noPadding ? 0 : "4px 8px" }}>
                {content}
            </div>
        );

        const isBye = side === 1 ? match.x1 : match.x2;
        if (isBye) return wrap(<span style={{ color: "var(--secondary-color)" }}>{t("Bye")}</span>);

        const roundOneMain = isMainDraw && match.round_number === 1;

        if (tournamentGrade === 1) {
            const p = side === 1 ? match.player_1_name : match.player_2_name;
            if (!p) return roundOneMain ? (is_authorized ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, true, true) : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>)) : wrap(renderSource(match, side));

            return typeof p === "string" ? wrap(<span className="uppercase0">{formatLastName(p)}</span>) : wrap(
                // Used to open this player's profile directly on click
                // (public view) - now just plain text, so the click always
                // falls through to the cell's own handleCellClick and opens
                // the match instead. Profile access for public view now
                // lives in the matches sheet (TournamentGridMatchesModal),
                // which has no competing "open the match" behavior on its
                // own name click.
                <span className="black-name">
                    {p?.first_name && <>{p.first_name[0]}. </>}<span className="uppercase0">{formatLastName(p.last_name)}</span>
                </span>
            );
        }

        if (tournamentGrade === 2) {
            const p1 = side === 1 ? match.player_1_name : match.player_2_name;
            const p2 = side === 1 ? match.player_3_name : match.player_4_name;

            if (!p1 || !p2) return roundOneMain ? (is_authorized ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, true, true) : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>)) : wrap(renderSource(match, side));

            // See the singles branch above - no longer a click target here
            // either, so the click always falls through to the cell's own
            // handleCellClick and opens the match.
            const fmt = (p) => (
                <span className="black-name">
                    {p.first_name ? `${p.first_name[0]}. ` : " "}<span className="uppercase0">{formatLastName(p.last_name)}</span>
                </span>
            );
            return wrap(<div>{fmt(p1)}<br />{fmt(p2)}</div>);
        }

        const team = side === 1
            ? match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`)
            : match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`);
        if (!team) return roundOneMain ? (is_authorized ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, true, true) : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>)) : wrap(renderSource(match, side));
        return wrap(<span className="black-name">{team}</span>);
    }, [tournamentGrade, isMainDraw, renderSource, t, is_authorized, history, location]);

    const renderSeed = useCallback((match, side) => {
        if (!match || match.round_number !== 1) return null;
        const seedVal = side === 1 ? match.seed1 : match.seed2;
        if (is_authorized) {
            return (
                <input
                    type="text"
                    maxLength={3}
                    defaultValue={seedVal || ""}
                    onBlur={e => handleSeedChange(match.id, side, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{paddingTop: "10px", paddingBottom: "10px", width: "38px", height: "39px", boxSizing: "border-box", textAlign: "center"}}
                />
            );
        }
        return seedVal ? <span>{seedVal}</span> : null;
    }, [is_authorized, handleSeedChange]);

    const renderSideScore = useCallback((match, side) => {
        if (!match || !match.sets) return null;
        const scores = match.sets.map((set, i) => {
            if (set.game_score_1 === null || set.game_score_2 === null) return null;
            let score = side === 1 ? set.game_score_1 : set.game_score_2;
            const tiebreak = side === 1 ? set.tiebreak_score_1 : set.tiebreak_score_2;
            
            let isLoser = false;
            if (set.game_score_1 !== set.game_score_2) {
                const setWinnerSide = set.game_score_1 > set.game_score_2 ? 1 : 2;
                isLoser = setWinnerSide !== side;
            } else if (set.tiebreak_score_1 != null && set.tiebreak_score_2 != null && set.tiebreak_score_1 !== set.tiebreak_score_2) {
                const setWinnerSide = set.tiebreak_score_1 > set.tiebreak_score_2 ? 1 : 2;
                isLoser = setWinnerSide !== side;
            } else if (match.winner_side !== null) {
                isLoser = match.winner_side !== side;
            }

            return (
                <div key={i} className={`row-games ${isLoser ? "winner" : ""}`} style={{ width: "15px", justifyContent: "center" }}>
                    {score}
                    {tiebreak != null && (
                        <sup className={`row-tb ${isLoser ? "winner" : ""}`}>{tiebreak}</sup>
                    )}
                </div>
            );
        }).filter(Boolean);

        if (scores.length === 0 && match.winner_side) {
            const isLoser = match.winner_side !== side;
            return <div className={`row-games ${isLoser ? "winner" : ""}`} style={{ width: "15px", justifyContent: "center" }}>{match.winner_side === side ? "+" : "-"}</div>;
        }
        return scores.length > 0 ? <div style={{ display: "flex", gap: "4px" }}>{scores}</div> : null;
    }, []);

    const renderSeedCell = useCallback((row_idx) => {
        const k = Math.floor(row_idx / 2);
        const side = (row_idx % 2 === 0) ? 1 : 2;
        const matchNum = matchStart(1) + k + 1;
        const match = matchByNum[matchNum];
        return renderSeed(match, side);
    }, [matchStart, matchByNum, renderSeed]);

    // === СБОРКА ТАБЛИЦЫ ===
    const headerCells = useMemo(() => {
        const cells = [];
        const showSeedColumn = isMainDraw && (is_authorized || hasAnySeed);
        cells.push(<td key="numbering-header" style={{ textAlign: "center", paddingLeft: "5px", paddingRight: "5px", borderBottom: "none" }}>№<br/><br/></td>);
        
        if (showSeedColumn) {
            cells.push(
                <td key="seed_header" style={{ textAlign: "center", width: is_authorized ? "auto" : "25px", borderBottom: "none", color: "var(--text-gray)", paddingTop: "15px" }}>
                    {is_authorized ? t("Seed") : t("S")}<br/><br/>
                </td>
            );
        }

        for (let r = 1; r <= R; r++) {
            cells.push(
                <td style={{paddingTop: '15px', borderLeft: 0, paddingLeft: 0}} key={`h_${r}`}>
                    {roundLabel(r)}<br/><br/>
                </td>
            );
            cells.push(
                <td key={`h_conn_${r}`} colSpan={2} style={{  borderRight: "none" }}></td>
            );
        }
        cells.push(<td key="spacer" style={{ width: '100%' }}></td>);
        return cells;
    }, [R, roundLabel, t, isMainDraw, is_authorized, hasAnySeed]);

    const bodyRows = useMemo(() => Array.from({ length: N }, (_, row_idx) => {
        const i = row_idx + 1;
        const cells = [];

        // Итерация по каждому раунду для заполнения ячеек строки
        for (let r = 1; r <= R; r++) {
            const period = Math.pow(2, r);
            const P1_offset = Math.pow(2, r - 1) - 1;
            const P2_offset = Math.pow(2, r - 1);

            const isP1 = (row_idx % period === P1_offset);
            const isP2 = (row_idx % period === P2_offset);
            const k = Math.floor(row_idx / period);
            const matchNum = matchStart(r) + k + 1;
            const match = matchByNum[matchNum];

            // upcoming matches used to fall back to var(--match) for non-admin viewers,
            // which is the same color as the page background and made them invisible —
            // keep the same completed-match background admins see until we add a
            // dedicated (e.g. shadow-based) treatment for upcoming matches
            const superWhiteBgColor = "var(--completed-match)";

            // Builds the absolutely-positioned shadow-card style shared by the seed,
            // player-name and score cells. `sides` marks which of the cell's 4 edges
            // are "internal" - shared with another merged card (seed/name/score
            // horizontally, the isP1/isP2 top/bottom pair vertically) - vs.
            // "external" (the block's own true outer edge). Internal sides get
            // inset:0 (flush, no gap) and no corner rounding there; external sides
            // get the usual 2px gap + rounded corner plus a real 1px border (same
            // var(--white-blue) as .tournament-card and the modal window's own
            // border) - unlike box-shadow, a border has a crisp exact edge with no
            // blur, so it can't bleed onto a flush neighbor and show as a seam
            // there. clip-path still trims the shadow itself at internal sides
            // too, since that part can still spread past its own box.
            const cardShadowStyle = (sides) => ({
                position: "absolute",
                top: sides.top ? 0 : 2,
                right: sides.right ? 0 : 2,
                bottom: sides.bottom ? 0 : 2,
                left: sides.left ? 0 : 2,
                backgroundColor: superWhiteBgColor,
                boxShadow: "var(--card-shadow)",
                borderTop: sides.top ? "none" : "1px solid var(--white-blue)",
                borderRight: sides.right ? "none" : "1px solid var(--white-blue)",
                borderBottom: sides.bottom ? "none" : "1px solid var(--white-blue)",
                borderLeft: sides.left ? "none" : "1px solid var(--white-blue)",
                borderRadius: `${!sides.top && !sides.left ? 4 : 0}px ${!sides.top && !sides.right ? 4 : 0}px ${!sides.bottom && !sides.right ? 4 : 0}px ${!sides.bottom && !sides.left ? 4 : 0}px`,
                clipPath: `inset(${sides.top ? 0 : -20}px ${sides.right ? 0 : -20}px ${sides.bottom ? 0 : -20}px ${sides.left ? 0 : -20}px)`,
                pointerEvents: "none"
            });

            // Флаги доступности действий для блока матча
            const bothPlayersExist = match && hasParticipant(match, 1) && hasParticipant(match, 2);

            // --- Колонка 1: Игрок ---
            if (isP1 || isP2) {
                const side = isP1 ? 1 : 2;
                // No longer gated to isMainDraw/is_authorized/r===1 - the
                // "who's in this slot" menu (openParticipantMenu) is now
                // available on every populated slot, for every viewer, same
                // as Групи/Розклад. Only the edit-oriented flows below
                // (canAddParticipant/isSlotEditable - adding/moving a
                // participant) stay admin+main-draw+round-1 only.
                // hasParticipant alone also passes for a bye (x1/x2 true, no real
                // name/roster to show) - openParticipantMenu handles that case on
                // its own now (just a Delete action, no profile/title), so a Bye
                // still counts as "openable" here instead of falling through to
                // handleCellClick (which opened the match, with no way to remove
                // the Bye at all).
                const isBye = match && (side === 1 ? match.x1 : match.x2);
                const canOpenActions = match && (isBye || hasParticipant(match, side));
                const canAddParticipant = r === 1 && is_authorized && match && !hasParticipant(match, side);
                const isSlotEditable = isMainDraw && r === 1 && !!match;
                // visual-only sizing (row height/padding below) - placement draws
                // (3-8/9-16/etc) have real round-1 matches too, just not editable
                // ones (isSlotEditable above stays isMainDraw-gated for that), and
                // looked visibly smaller/cramped there than the main draw for no
                // real reason.
                const hasR1Match = r === 1 && !!match;
                // r>1's own ps_ cell only ever gets rowSpan 2 (its P1 side, always an
                // odd outer row_idx) or rowSpan 1 (its P2 side, always even) - a P2
                // cell with rowSpan 1 has just ONE outer <tr>'s own budget to fit in,
                // not two. The old 44px-content + 5px-all-round-padding size here
                // (54px total) fit fine inside a 2-row budget but overflowed a 1-row
                // one - visibly taller than every neighboring row, and only on
                // whichever specific rows a later round's P2 cell happened to land
                // on. Sizing every round the same as round-1's own real slots (48px,
                // 0 vertical padding - see this div's own comment below) keeps every
                // round-1 row the same height regardless of which rounds' cells share
                // it, the same way giving round-1 that size already matched Groups.
                const roomySlot = hasR1Match || r > 1;
                const slotOccupied = isSlotEditable && hasParticipant(match, side);
                const isThisPicked = isSlotEditable && pickedSlot?.matchId === match.id && pickedSlot?.side === side;
                const isThisPickerTarget = isSlotEditable && pickerTarget?.matchId === match.id && pickerTarget?.side === side;
                // loadingSlots is this component's own (drag/move only) - loadingCells
                // is the parent's, set while an add-participant request from the
                // picker modal/candidate-place flow is in flight for this same slot.
                const isSlotLoading = isSlotEditable && !!(loadingSlots[slotKey(match.id, side)] || loadingCells?.[slotKey(match.id, side)]);
                const content = isSlotLoading
                    ? <span className="player cursor" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}><span className="loading-dots"><span></span><span></span><span></span></span></span>
                    : (pickedSlot || pickedCandidate) && isSlotEditable && !slotOccupied
                    ? <span className="player cursor group-add-participant bracket-drop-here">{t("Drop here")}</span>
                    // dim just the participant, not the .picked td (which also carries the
                    // cell's own backgroundColor - applying opacity there faded that
                    // background in too, reading as a separate highlighted box)
                    : <span style={{ opacity: isThisPicked ? 0.5 : 1 }}>
                        {match ? renderSide(match, side, isMainDraw, row_idx + 1, isThisPickerTarget) : (r === 1 ? <span style={{ color:"#aaa" }}>{t("TBD")}</span> : null)}
                    </span>;

                // Клик по имени участника открывает меню действий (или модалку добавления/
                // перенос слота) только на 1 раунде основной сетки (isSlotEditable) - там
                // участников реально добавляют/удаляют. На любом другом раунде клик по
                // имени, как и по счёту/пустому месту строки, открывает сам матч - они
                // больше не делят один обработчик на весь td (см. name/score-подячейки ниже).
                // Team tournaments, touch: the roster menu moved to a long-press
                // (handleNameTouchStart above) - a plain tap here is the same touch
                // gesture that press was, so it falls through without stopping
                // propagation, same as any other empty/non-actionable click, and
                // lets handleCellClick below open the match instead. Public view:
                // every occupied slot. Admin view: only non-editable slots
                // (isSlotEditable false - later rounds, placement draws) - an
                // editable round-1 slot's long-press is already spoken for
                // (handleTouchStart above, pick-up-to-move), so this stays
                // out of its way there.
                const nameOpensMatchInstead = (!is_authorized || !isSlotEditable) && tournamentGrade === 3 && canOpenActions && isTouchDevice;

                const handleNameClick = (e) => {
                    if (isSlotLoading) return;
                    if (is_authorized && pickedSlot) {
                        e.stopPropagation();
                        if (isSlotEditable) handleCellTap(match.id, side);
                        return;
                    }
                    // A candidate was picked from the still-open panel (no
                    // slot targeted yet) - canAddParticipant already means
                    // "empty, editable, admin" here, so reuse it to gate
                    // placement; anything else (an occupied name) cancels
                    // the pick instead of opening its actions menu/the
                    // match - was a bare no-op, but the panel can stay open
                    // across several picks, so clicking through a few
                    // candidates while just browsing left this stuck on and
                    // every click on an occupied name silently did nothing.
                    if (is_authorized && pickedCandidate) {
                        e.stopPropagation();
                        if (canAddParticipant) onPlaceCandidate(match.id, side);
                        else onCancelCandidate?.();
                        return;
                    }
                    if (nameOpensMatchInstead) {
                        if (nameLongPressFiredRef.current) {
                            // the long-press already opened the menu for this
                            // same touch - don't also let the tap that follows
                            // it (touchend's synthetic click) open the match
                            // underneath.
                            nameLongPressFiredRef.current = false;
                            e.stopPropagation();
                        }
                        // else: a plain tap, no stopPropagation - falls through
                        // to handleCellClick below, same as any other
                        // non-actionable click, which opens the match.
                        return;
                    }
                    // Click always opens the actions menu (profile/delete,
                    // plus "Move" when it's an editable round-1 slot - see
                    // openParticipantMenu's allowMove param), same as
                    // Groups' own participant click - consistent everywhere,
                    // touch or desktop. Long-press (handleTouchStart above)
                    // is the separate, dedicated gesture for picking a slot
                    // straight up to move it.
                    if (canAddParticipant) {
                        e.stopPropagation();
                        handlePlayerClick(match, side);
                    } else if (canOpenActions && isSlotEditable && is_authorized) {
                        e.stopPropagation();
                        openParticipantMenu(match, side, { allowMove: slotOccupied });
                    }
                    // иначе - не глушим клик, пусть всплывёт до handleCellClick на
                    // внешнем td и откроет сам матч.
                };

                const handleCellClick = (e) => {
                    if (isSlotLoading) return;
                    if (is_authorized && pickedSlot) {
                        if (isSlotEditable) handleCellTap(match.id, side);
                        return;
                    }
                    if (is_authorized && pickedCandidate) {
                        if (isSlotEditable && !slotOccupied) onPlaceCandidate(match.id, side);
                        else onCancelCandidate?.();
                        return;
                    }
                    if (bothPlayersExist) {
                        handleMatchClick(matchNum);
                    }
                };

                const rs = (r > 1 && row_idx % 2 === 1 && row_idx !== N - 1) ? 2 : 1;
                const showSeedColumn = isMainDraw && (is_authorized || hasAnySeed);

                if (r === 1 && showSeedColumn) {
                    // same internal/external split as cardShadowStyle's own sides - the
                    // content has to sit inside the same inset box the shadow card
                    // draws, or it pokes out past the card's border on the external
                    // edge(s) instead of stopping at it.
                    const seedSides = { top: isP2, right: true, bottom: isP1, left: false };
                    cells.push(
                        <td
                            key={`seed_${r}`}
                            style={{
                                position: "relative",
                                textAlign: "center",
                                padding: 0,
                                // 50px left only 36px free once the content div's left inset
                                // (2px, the card's external edge) and "0 6px" padding are taken
                                // out - 2px short of the 38px input, which then bled out past
                                // the card's left border (the only side without a border to
                                // hide it against, since the right side is flush with the
                                // player cell). minWidth, not width: this cell's own content is
                                // entirely absolutely-positioned (see below), so it has ~0
                                // intrinsic content width - plain "width" is only a soft hint in
                                // auto table layout and this table's other much-hungrier columns
                                // (name+score, connectors) were winning the fight for space,
                                // collapsing this column to a few px despite the "width" set.
                                minWidth: is_authorized ? "58px" : "25px",
                                // real divider between this match's two players (top/bottom
                                // pair) - deliberately a border, not part of the shadow card,
                                // so it stays a crisp line instead of the faint blur
                                // clip-path leaves at that seam
                                borderBottom: isP2 ? "none" : "1px solid var(--line)"
                            }}
                        >
                            <div style={cardShadowStyle(seedSides)} />
                            {/* Same top/bottom inset (2px) on both P1 and P2 rows, unlike the
                                shadow card above (which needs the asymmetric 0/2 split so its
                                own drawn edge sits flush on the internal/shared side) - this
                                div only needs to stay inside that drawn box, not match it
                                exactly, and 2px both sides always does that safely (shadow's
                                own box is never shorter than this one). Matching insets center
                                the seed input on the row itself instead of 1px off it - was
                                mirroring seedSides' own asymmetry, which put the input 1px
                                off the button's already-centered position next to it (see
                                cardShadowStyle's own comment above for the asymmetry's actual
                                reason). left/right insets stay asymmetric - horizontal
                                position not row-height related. */}
                            <div style={{
                                position: "absolute",
                                top: 2,
                                right: seedSides.right ? 0 : 2,
                                bottom: 2,
                                left: seedSides.left ? 0 : 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                // more left than right padding - shifts the seed value off
                                // the card's left border (the only real, visible edge here;
                                // the right side is flush with the player cell) instead of
                                // dead centering it, which read as cramped against that
                                // border for 2-character seeds ("A1"). Horizontal total is
                                // unchanged, so this doesn't need the cell any wider.
                                padding: is_authorized ? "0 4px 0 8px" : "0 6px 0 14px"
                            }}>
                                {renderSeedCell(row_idx)}
                            </div>
                        </td>
                    );
                }

                cells.push(
                    <td
                        key={`ps_${r}`}
                        // bracket-player-cell/bracket-score-cell kept only for any external
                        // targeting (e.g. tests) - their own border/background rules are
                        // neutralized below the same way the old separate cells already
                        // neutralized them. searchStateClass on the whole cell (not just
                        // the name inside it, via renderSide) - dims/highlights the score
                        // and #N badge next to it too, as one match block.
                        className={`bracket-player-cell bracket-score-cell ${isP1 ? "p1" : "p2"} ${canAddParticipant || bothPlayersExist ? "bracket-pointer" : ""} ${searchStateClass(matchSearchState(match))}`}
                        onClick={handleCellClick}
                        onDragOver={isSlotEditable && !isSlotLoading ? e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } : undefined}
                        onDrop={isSlotEditable && !isSlotLoading ? e => handleDrop(e, match.id, side) : undefined}
                        rowSpan={rs}
                        style={{
                            minWidth: r === 1 ? (is_authorized ? (isMainDraw ? "160px" : "130px") : hasAnySeed ? "150px" : "140px") : "130px",
                            position: "relative",
                            // background now lives on the absolutely-positioned card below,
                            // not the td itself - box-shadow on a table cell under
                            // border-collapse:collapse gets clipped by the neighboring cell
                            // instead of spilling over it (same fix as Bracket.js's own
                            // player td). Explicit "transparent" (not just omitted) so it
                            // also beats .bracket-player-cell.p1/.p2's own CSS background.
                            backgroundColor: "transparent",
                            // .bracket-player-cell/.bracket-score-cell/.p1/.p2 (index.css)
                            // still set their own border-left/top/bottom - explicit "none"
                            // beats those too, same reasoning as backgroundColor above. The
                            // shadow card is the only visual boundary now, except for the
                            // real divider between this match's two players (see the seed
                            // cell's own comment on this same property).
                            border: "none",
                            borderBottom: isP2 ? "none" : "1px solid var(--line)",
                            padding: 0,
                            cursor: is_authorized && isSlotEditable ? (isSlotLoading ? "default" : (pickedSlot || pickedCandidate) ? "crosshair" : slotOccupied ? "grab" : undefined) : undefined,
                            pointerEvents: isSlotLoading ? "none" : undefined
                        }}
                    >
                        {/* Name and score used to be two separate td's, each with their own
                            shadow card flush against the other - box-shadow's blur has no
                            hard edge, so however precisely the two boxes lined up, each
                            still bled a little onto its neighbor and showed as a seam right
                            at the shared edge (clip-path trimmed most of it, but not
                            reliably enough at every zoom/DPI). One cell, one shadow card,
                            no seam possible - "right" is external now (this cell's own true
                            right edge); "left" is internal only when a seed cell is merged
                            in on that side too (r===1 && showSeedColumn). */}
                        <div style={cardShadowStyle({ top: isP2, right: false, bottom: isP1, left: r === 1 && showSeedColumn })} />
                        <div style={{ position: "relative", height: "100%", display: "flex", alignItems: "center" }}>
                            {/* Compact mode: match number moved to the start of the match
                                (before the pair, not after) instead of the connector cell
                                that used to hold it - connector line/rowSpan itself is
                                untouched (see the two `!compactTable &&` guards on its own
                                label below). P1 and P2 are two separate <td>'s (not one
                                rowSpan-merged cell - see cardShadowStyle's own comment
                                above), so there's no single DOM node that's naturally "the
                                pair" - anchored to P1's own card instead, bottom-right
                                corner (near the shared divider border with P2, right where
                                the connector used to start) so it reads as belonging to the
                                match as a whole, not just to P1's name. */}
                            {compactTable && isP1 && (
                                <span style={{ position: "absolute", right: "4px", bottom: "-2px", fontSize: "11px", color: "var(--secondary-color)", lineHeight: 1, pointerEvents: "none" }}>#{matchNum}</span>
                            )}
                            {/* round-1 editable slots can show the group-add-participant button
                                (39px min-height) when empty - give the row that much room always,
                                so it doesn't grow for the button and then shrink back down the
                                moment a name fills it in (matches Bracket.js's same fix). 48px
                                matches TournamentGroupTable.js's own equivalent cell exactly (same
                                button, same class) - was 54px, taller than Groups for no reason
                                other than never having been checked against it. */}
                            <div style={{
                                flex: 1,
                                minWidth: 0,
                                // matches the old outer td padding (5px all around, or "0 5px"
                                // for editable slots so the inner table's own fixed height alone
                                // drives row height) - moved here since the td itself is now
                                // padding:0 (the shadow card above needs the full cell to inset from)
                                padding: roomySlot ? "0 5px" : "5px"
                            }}>
                                <table className="bracket-inner-table" style={{ width: "100%", height: roomySlot ? "48px" : "44px" }}>
                                    <tbody>
                                    <tr>
                                        <td
                                            draggable={is_authorized && isSlotEditable && slotOccupied && !isTouchDevice && !pickedSlot && !isSlotLoading && !pickerTarget && !pickedCandidate}
                                            onDragStart={isSlotEditable && slotOccupied && !isSlotLoading && !pickerTarget && !pickedCandidate ? e => handleDragStart(e, match.id, side) : undefined}
                                            onTouchStart={
                                                is_authorized && isSlotEditable && slotOccupied && !isSlotLoading && !pickerTarget && !pickedCandidate
                                                    ? e => handleTouchStart(e, match.id, side)
                                                    : (nameOpensMatchInstead && !isSlotLoading
                                                        ? e => handleNameTouchStart(e, match, side)
                                                        : undefined)
                                            }
                                            onContextMenu={isSlotEditable && slotOccupied ? e => e.preventDefault() : undefined}
                                            onClick={handleNameClick}
                                            style={{ textAlign: "left" }}
                                        >
                                            {content}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ flex: "none", display: "flex", alignItems: "center", paddingRight: "8px" }}>
                                {renderSideScore(match, side)}
                            </div>
                        </div>
                    </td>
                );
            } else {
                const rs = (r > 1 && row_idx % 2 === 1 && row_idx !== N - 1) ? 2 : 1;
                cells.push(<td key={`ps_${r}`} rowSpan={rs} style={{ borderLeft: "none" }}></td>);
            }

            // --- Колонки 3 и 4: Соединительные линии ---
            if (r <= R) {
                const rs_conn = (row_idx % 2 === 1 && row_idx !== N - 1) ? 2 : 1;
                if (r < R) {
                    const k_block = Math.floor((i - 1) / Math.pow(2, r));
                    const center_y = Math.pow(2, r) * k_block + Math.pow(2, r - 1);
                    const next_y = Math.pow(2, r + 1) * Math.floor(k_block / 2) + Math.pow(2, r);

                    const isCenter = (i === center_y);
                    const isNext = (i === next_y);
                    const isBetween = (i > Math.min(center_y, next_y) && i <= Math.max(center_y, next_y));

                    cells.push(
                        <td
                            key={`c1_${r}`}
                            rowSpan={rs_conn}
                            className={`bracket-conn-1 ${isCenter ? "bracket-line-bottom" : ""} ${isBetween ? "bracket-line-right" : ""}`}
                            style={{ minWidth: "25px", textAlign: "left" }}
                        >
                            {!compactTable && isCenter && <label style={{paddingLeft: "5px", paddingBottom: "4px"}}>#{matchNum}</label>}
                        </td>
                    );

                    cells.push(
                        <td
                            key={`c2_${r}`}
                            rowSpan={rs_conn}
                            className={`bracket-conn-2 ${isNext ? "bracket-line-bottom" : ""}`}
                            style={{ minWidth: "25px" }}
                        ></td>
                    );
                } else {
                    const winnerRowIdx = Math.pow(2, R - 1) - 1;
                    if (row_idx === winnerRowIdx) {
                        const finalMatchNum = matchStart(R) + 1;
                        cells.push(
                            <td key={`c1_${r}`} rowSpan={rs_conn} className="bracket-conn-1 bracket-line-bottom" style={{ minWidth: "25px", textAlign: "left" }}>
                                {!compactTable && <label style={{paddingLeft: "5px", paddingBottom: "4px"}}>#{finalMatchNum}</label>}
                            </td>
                        );
                        cells.push(
                            <td key={`c2_${r}`} rowSpan={rs_conn} className="bracket-conn-2 bracket-line-bottom" style={{ minWidth: "25px" }}></td>
                        );
                    } else if (row_idx === winnerRowIdx + 1) {
                        cells.push(
                            <td key={`c1_${r}`} rowSpan={rs_conn} className="bracket-conn-1" style={{ minWidth: "25px" }}></td>
                        );
                        cells.push(
                            <td key={`c2_${r}`} rowSpan={rs_conn} className="bracket-conn-2" style={{ minWidth: "25px" }}></td>
                        );
                    } else {
                        cells.push(<td key={`c1_${r}`} rowSpan={rs_conn} style={{ minWidth: "25px" }}></td>);
                        cells.push(<td key={`c2_${r}`} rowSpan={rs_conn} style={{ minWidth: "25px" }}></td>);
                    }
                }
            }
        }

        const rsWinner = (row_idx % 2 === 1 && row_idx !== N - 1) ? 2 : 1;
        cells.push(<td key="spacer" rowSpan={rsWinner} style={{ border: 'none', borderBottom: row_idx === Math.floor((N - 1) / 2) ? "1px solid var(--line)" : "none" }}></td>);

        cells.unshift(
            <td key="numbering"
                className={`bracket-numbering-cell ${row_idx % 2 === 0 ? "p1" : "p2"}`}
                style={{
                    backgroundColor: "transparent",
                    background: "transparent",
                    textAlign: "center",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    borderTop: "none",
                    borderBottom: row_idx % 2 === 0 ? "1px solid var(--line)" : "none",
                    borderLeft: "none",
                    borderRight: "none"
                }}>
                <label className={row_idx % 2 === 0 ? "number-bottom" : "number-top"} style={{ marginTop: row_idx % 2 === 1 ? "-8px" : undefined, display: "block" }}>
                    {row_idx + 1}
                </label>
            </td>
        );
        // cells.unshift(<td key="spacer-left" style={{ border: 'none' }}></td>);
        const finalCells = row_idx === 0 ? cells.map(c => React.cloneElement(c, { style: { ...c.props.style, paddingTop: 0 } })) : cells;
        const rows = [<tr key={row_idx}>{finalCells}</tr>];

        if (row_idx % 2 === 1 && row_idx !== N - 1) {
            const showSeedColumn = isMainDraw && (is_authorized || hasAnySeed);
            const gapCells = [];
            gapCells.push(<td key="gap_n" style={{ border: "none", padding: 0 }}></td>);
            if (showSeedColumn) gapCells.push(<td key="gap_seed" style={{ border: "none", padding: 0 }}></td>);
            gapCells.push(<td key="gap_p" style={{ border: "none", padding: 0 }}></td>);

            rows.push(
                <tr key={`gap_${row_idx}`} style={{ height: "5px" }}>
                    {gapCells}
                </tr>
            );
        }
        return rows;
    }).flat(), [N, R, matchStart, matchByNum, renderSide, isMainDraw, t, is_authorized, hasAnySeed, hasParticipant, handlePlayerClick, id, onDeleteParticipant, handleMatchClick, renderSideScore, renderSeedCell,
        pickedSlot, pickerTarget, pickedCandidate, onPlaceCandidate, isTouchDevice, handleDragStart, handleDrop, handleTouchStart, handleCellTap, loadingSlots, loadingCells, compactTable, openParticipantMenu, handleNameTouchStart, matchSearchState]);

    return (
        <div style={{ position: "relative" }}>
            {pickedSlot && (
                <div
                    onClick={() => setPickedSlot(null)}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {/* Same bottom pill as pickedSlot above (moving an already-seated
                participant) - pickedCandidate is the equivalent "in hand"
                state for a brand new one picked from the picker modal/panel,
                but had no visible cancel affordance of its own: onCancelCandidate
                was wired in (see the slot click handlers above) yet nothing
                ever rendered a control that actually called it. */}
            {pickedCandidate && (
                <div
                    onClick={() => onCancelCandidate?.()}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {!isMainDraw && is_authorized && (
                <div
                    className="delete-team"
                    style={{zIndex: 2, position: "absolute", top: "12px", right: "15px", cursor: "pointer" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(t("Are you sure you want to delete this bracket?"))) {
                            api.delete(`/api/v1.0/tournaments/${id}/draw-stage/draws/${draw.id}`)
                                .then(() => toast.success(t("Bracket deleted")))
                                .catch((error) => console.error(error));
                        }
                    }}
                >
                    <FeatherIcon icon="x" size={14} />
                </div>
            )}
            <div className="bracket-classic-table">
                <table className="main-bracket" border="0" style={{ width: "100%" }}>
                    <thead>
                    <tr>
                        <th colSpan={100} className={is_authorized ? "authorized-header" : ""}>
                            <div style={{ display: "flex", justifyContent: "flex-start", marginLeft: "5px", color: is_authorized ? ("var(--primary-color)") : "#fff" }}>
                                <span className="bracket-sticky-left">{t("Bracket of")} {draw.number} {t("place")}</span>
                            </div>
                        </th>
                    </tr>
                    <tr className="group-header">{headerCells}</tr>
                    </thead>
                    <tbody className="tbody_dop">
                    {bodyRows}
                    <tr><td colSpan={100} style={{ height: "25px", padding: 0, border: "none" }}></td></tr>
                    </tbody>
                </table>
            </div>
            <ParticipantActionsModal
                isOpen={!!actionsModal}
                onClose={() => setActionsModal(null)}
                title={actionsModal?.title}
                actions={actionsModal?.actions || []}
            />
        </div>
    );
}

// memo - see Bracket.js's own identical comment on its export.
export default React.memo(BracketClassic);